import
{ Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from "@angular/core";
import { AbstractControl, FormArray, FormGroup } from "@angular/forms";
import { Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { SelectionChange, SelectionModel } from "@angular/cdk/collections";
import { MatTableDataSource } from "@angular/material/table";

import { LgdFormControlFactory, setEnabled } from "@tylertech/lgd-reactive-forms";
import { DataSearchService } from "@tylertech/data-load-module";
import { InputParameterService } from "@tylertech/search-module";
import
{
    Bucket,
    IInfoCardData,
    ISearchCriteria,
    RequisitionSearchViewModel,
} from "@tylertech/tyler-api-types";

import { RequisitionSearchService } from "../../services";

type RequisitionColumn = "Selected" | "RequisitionNumber" | "Date" | "Vendor" | "Description" | "Amount";
type FilterState<T extends string = string> = Partial<Record<T, string>>;

@Component({
    selector: "requisition-selection-review",
    templateUrl: "./requisitionSelectionReview.tmpl.html",
    styleUrls: [
        "./requisitionSelectionReview.component.scss"
    ],
    standalone: false
})
export class RequisitionSelectionReviewComponent implements OnInit, OnDestroy
{
    private $requisitionSearchService = inject(RequisitionSearchService);
    private $dataSearchService = inject<DataSearchService<IInfoCardData>>(DataSearchService);
    private $inputParameterService = inject(InputParameterService);

    private readonly destructor$ = new Subject<void>();
    private readonly tableRowsDestructor = new Subject<void>();
    private readonly filterState: FilterState<RequisitionColumn> = {};
    readonly tableFormGroup: FormGroup;
    readonly tableFilterFormGroup: FormGroup;
    readonly issueDateGroup: FormGroup;

    @Input() readonly issueDate: Date;
    @Output() private issueDateChange = new EventEmitter<Date>();

    @Input() readonly searchDepartment: string | undefined;
    @Input() readonly searchIssueDateFrom: string | undefined;
    @Input() readonly searchIssueDateTo: string | undefined;
    @Input() readonly searchVendor: string | undefined;
    @Input() readonly searchNumber: string | undefined;

    @Input() readonly selectedRequisitions: ReadonlyArray<RequisitionSearchViewModel> | undefined;

    @Output() private readonly selectionChanged = new EventEmitter<Array<RequisitionSearchViewModel>>();

    isLoaded: boolean;
    message: string = "No Data";

    readonly columnNames: ReadonlyArray<RequisitionColumn> = [
        "Selected",
        "RequisitionNumber",
        "Date",
        "Vendor",
        "Description",
        "Amount"
    ];

    tableData: MatTableDataSource<RequisitionSearchViewModel>;

    private selectionManager: SelectionModel<RequisitionSearchViewModel>;

    constructor()
    {
        const initialSelection = [];
        const allowMultiSelect = true;
        this.selectionManager = new SelectionModel<RequisitionSearchViewModel>(allowMultiSelect, initialSelection);
        this.tableData = new MatTableDataSource<RequisitionSearchViewModel>();
        this.tableData.filterPredicate = (data: RequisitionSearchViewModel, filter: string): boolean =>
        {
            const state = JSON.parse(filter) as FilterState<RequisitionColumn>;
            return Object.entries(state)
                .reduce(
                    (result: boolean, [column, filterValue]: [string, string]): boolean =>
                    {
                        if (!result || !filterValue)
                            return result;

                        const value: string | undefined = data[column]?.toString().toLowerCase();
                        if (!value || value.indexOf(filterValue) === -1)
                        {
                            return false;
                        }
                        return true;
                    },
                    true);
        };

        this.tableFilterFormGroup = new FormGroup({
            selectAll: LgdFormControlFactory.checkbox(),
            requisitionNumberFilter: LgdFormControlFactory.text(),
            dateFilter: LgdFormControlFactory.text(),
            vendorFilter: LgdFormControlFactory.text(),
            descriptionFilter: LgdFormControlFactory.text(),
            amountFilter: LgdFormControlFactory.text(),
        });

        this.tableFormGroup = new FormGroup({
            rows: new FormArray([
                this.tableFilterFormGroup
            ])
        });

        this.issueDateGroup = new FormGroup({
            issueDate: LgdFormControlFactory.date(undefined, () => !this.useRequisitionDate),
            useRequisitionDate: LgdFormControlFactory.checkbox(),
        });
    }

    private get selectAllControl(): AbstractControl<boolean> { return this.tableFilterFormGroup.get("selectAll"); }
    private get requisitionNumberFilterControl(): AbstractControl<string> { return this.tableFilterFormGroup.get("requisitionNumberFilter"); }
    private get dateFilterControl(): AbstractControl<string> { return this.tableFilterFormGroup.get("dateFilter"); }
    private get vendorFilterControl(): AbstractControl<string> { return this.tableFilterFormGroup.get("vendorFilter"); }
    private get descriptionFilterControl(): AbstractControl<string> { return this.tableFilterFormGroup.get("descriptionFilter"); }
    private get amountFilterControl(): AbstractControl<string> { return this.tableFilterFormGroup.get("amountFilter"); }

    private get rowsArray(): FormArray { return this.tableFormGroup.get("rows") as FormArray; }

    private get issueDateControl(): AbstractControl<Date> { return this.issueDateGroup.get("issueDate"); }
    private get useRequisitionDateControl(): AbstractControl<boolean> { return this.issueDateGroup?.get("useRequisitionDate"); }

    private get selectAllRequisitions(): boolean { return this.selectAllControl.value; }
    private get useRequisitionDate(): boolean { return this.useRequisitionDateControl?.value || false; }

    private get selected(): number { return this.selectionManager.selected.length; }

    get hasData(): boolean { return this.tableData.data?.length > 0; }

    get selectTitle(): string { return this.selectAllRequisitions ? "Remove All" : "Select All"; }

    get GrandTotal(): number { return this.selectedRequisitions?.reduce((a, x) => a + x.TotalAmount, 0) || 0; }

    ngOnDestroy(): void
    {
        this.tableRowsDestructor.next();
        this.tableRowsDestructor.complete();

        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        this.$dataSearchService.GetCriteria("Requisition.InfoCardProvider")
            .then((result: ISearchCriteria<IInfoCardData>) =>
            {
                const criteria = this.modifyCriteria(result as unknown as ISearchCriteria<RequisitionSearchViewModel>);

                return this.$requisitionSearchService.SearchRequisitionsReadyForPurchaseOrder(criteria);
            })
            .then((result: ISearchCriteria<RequisitionSearchViewModel>) =>
            {
                this.initializeTableRows(result.SearchResult);

                this.selectedRequisitions?.map(x =>
                {
                    this.selectionManager.toggle(x);
                });

                this.message = result.Settings["Message"] || "No Data";
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });

        this.subscribeTo(this.selectionManager.changed, (_selectionChange: SelectionChange<RequisitionSearchViewModel>) =>
        {
            this.selectionChanged.emit(this.selectionManager.selected);
        });

        this.subscribeToValueChanges(this.selectAllControl, (_newValue: boolean) =>
        {
            this.selectionManager.setSelection(...this.selectedRequisitions);
        });

        this.subscribeToValueChanges(this.requisitionNumberFilterControl, (newValue: string) =>
        {
            this.filterState.RequisitionNumber = newValue.toLowerCase();

            this.updateTableFilter();
        });

        this.subscribeToValueChanges(this.dateFilterControl, (newValue: string) =>
        {
            this.filterState.Date = newValue.toLowerCase();

            this.updateTableFilter();
        });

        this.subscribeToValueChanges(this.vendorFilterControl, (newValue: string) =>
        {
            this.filterState.Vendor = newValue.toLowerCase();

            this.updateTableFilter();
        });

        this.subscribeToValueChanges(this.descriptionFilterControl, (newValue: string) =>
        {
            this.filterState.Description = newValue.toLowerCase();

            this.updateTableFilter();
        });

        this.subscribeToValueChanges(this.amountFilterControl, (newValue: string) =>
        {
            this.filterState.Amount = newValue.toLowerCase();

            this.updateTableFilter();
        });

        this.issueDateControl.setValue(this.issueDate);
        this.subscribeToValueChanges(this.issueDateControl, (newValue: Date) =>
        {
            if (!this.useRequisitionDate)
            {
                this.issueDateChange.emit(new Date());
            }
            else if (this.selected === 1)
            {
                this.issueDateChange.emit(newValue);
            }
            else if (this.selected > 1)
            {
                this.issueDateChange.emit(null);
            }

            // TODO - What if this.selected < 1?
        });

        this.subscribeToValueChanges(this.useRequisitionDateControl, (newValue: boolean) =>
        {
            setEnabled(this.issueDateControl, newValue);
        });
    }

    private subscribeTo<T = unknown>(observable: Observable<T>, subscriptionFn: (newValue: T) => void, destructor?: Subject<void>): void
    {
        observable.pipe(takeUntil(destructor || this.destructor$))
            .subscribe(x =>
            {
                subscriptionFn(x);
            });
    }

    private subscribeToValueChanges<T = unknown>(control: AbstractControl<T>, subscriptionFn: (newValue: T) => void, destructor?: Subject<void>): void
    {
        this.subscribeTo(control.valueChanges, subscriptionFn, destructor);
    }

    private updateTableFilter(): void
    {
        this.tableData.filter = JSON.stringify(this.filterState);
    }

    private initializeTableRows(values: Array<RequisitionSearchViewModel>): void
    {
        this.tableRowsDestructor.next();
        this.rowsArray.clear();
        this.rowsArray.push(this.tableFilterFormGroup);

        this.tableData.data = values;
        this.tableData.data
            .forEach(
                r =>
                {
                    const selectRowControl = LgdFormControlFactory.checkbox();
                    const rowFormGroup = new FormGroup({ selected: selectRowControl });
                    this.rowsArray.push(rowFormGroup);

                    this.subscribeToValueChanges(selectRowControl, (_newValue: boolean) =>
                    {
                        this.selectionManager.toggle(r);
                    });
                });
    }

    private modifyCriteria(criteria: ISearchCriteria<RequisitionSearchViewModel>): ISearchCriteria<RequisitionSearchViewModel>
    {
        criteria.Size = 0;
        criteria.Settings = { RequisitionsForPO: true };

        if (this.searchDepartment)
        {
            criteria.Facets.forEach(x =>
            {
                if (x.Name === "Department")
                {
                    x.Buckets.push(<Bucket>{
                        Selected: true,
                        Key: this.searchDepartment,
                    });
                }
            });
        }

        criteria.Facets.forEach(v =>
        {
            if (v.IndexFieldName === "ApprovalStatus")
            {
                let found = false;
                v.Buckets.forEach(b =>
                {
                    if (b.Key === "Approved")
                    {
                        found = true;
                        b.Selected = true;
                    }
                });

                if (!found)
                {
                    v.Buckets.push(<Bucket>{
                        Key: "Approved",
                        Selected: true,
                    });
                }
            }
        });

        if (this.searchIssueDateFrom && this.searchIssueDateTo)
        {
            criteria.Parameters.forEach(x =>
            {
                if (x.FieldId === "IssueDate")
                {
                    x.Value = {
                        MinDate: this.searchIssueDateFrom,
                        MaxDate: this.searchIssueDateTo,
                    };
                }
            });
        }
        else if (this.searchIssueDateFrom)
        {
            criteria.Parameters.forEach(x =>
            {
                if (x.FieldId === "IssueDate")
                {
                    x.Value = { MinDate: this.searchIssueDateFrom };
                }
            });
        }
        else if (this.searchIssueDateTo)
        {
            criteria.Parameters.forEach(x =>
            {
                if (x.FieldId === "IssueDate")
                {
                    x.Value = { MaxDate: this.searchIssueDateTo };
                }
            });
        }

        criteria.Parameters.push(
            this.$inputParameterService.createBooleanParameter({
                Label: "Packet",
                FieldId: "InAPacket",
                Value: false,
            })
        );

        if (this.searchVendor)
        {
            criteria.QueryString = this.searchVendor;
        }

        if (this.searchNumber)
        {
            criteria.QueryString = this.searchNumber;
        }

        return criteria;
    }
}
